// @ts-strict-ignore
import { useCallback, useState } from 'react';
import { Button, ButtonVariant, Modal } from '@usealloy/component-library';
import type { BaseDocumentUploadProps } from './DocumentUploadContent';
import { DocumentUploadContent } from './DocumentUploadContent';

/**
 * Props for DocumentUploadModal.
 * Extends BaseDocumentUploadProps with modal-specific props (isOpen, onClose, onUpload).
 */
interface DocumentUploadModalProps extends BaseDocumentUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
}

/**
 * Modal wrapper for document upload functionality.
 * Maintains backward compatibility with existing usage patterns.
 * Uses DocumentUploadContent for the core upload logic.
 */
export const DocumentUploadModal = ({
  caseToken,
  documentTypes,
  entities,
  evaluationToken,
  investigationToken,
  isOpen,
  onClose,
  onUpload,
}: DocumentUploadModalProps) => {
  const isMultiEntityContext = Object.values(entities).length > 1;
  const singleEntity = !isMultiEntityContext ? Object.values(entities)[0] : null;

  const [submitState, setSubmitState] = useState(
    { onSubmit: null as ((e?: any) => void) | null, uploading: false, disabled: true },
  );

  const handleSubmitStateChange = useCallback(setSubmitState, []);

  const handleUploadComplete = useCallback(() => {
    onUpload();
    onClose();
  }, [onUpload, onClose]);

  return (
    <Modal
      header={singleEntity?.entity_name || singleEntity?.entity_token
        ? `${singleEntity.entity_name || singleEntity.entity_token}: Upload Documents`
        : 'Upload Documents'}
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      secondaryAction={<Button onClick={onClose}>Cancel</Button>}
      primaryAction={(
        <Button
          onClick={submitState.onSubmit ?? undefined}
          variant={ButtonVariant.primary}
          loading={submitState.uploading}
          disabled={submitState.disabled}
        >
          Upload
        </Button>
      )}
    >
      <DocumentUploadContent
        caseToken={caseToken}
        documentTypes={documentTypes}
        entities={entities}
        evaluationToken={evaluationToken}
        investigationToken={investigationToken}
        onUploadComplete={handleUploadComplete}
        onCancel={onClose}
        onSubmitStateChange={handleSubmitStateChange}
        showCancelButton={false}
        showUploadButton={false}
      />
    </Modal>
  );
};
